import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Checkbox,
  LinearProgress,
  Radio,
  Grow,
  Slide,
} from "@mui/material";
import { keyframes } from "@mui/system";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupIcon from "@mui/icons-material/Group";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import StoreIcon from "@mui/icons-material/Store";
import PlaceIcon from "@mui/icons-material/Place";
import PublicIcon from "@mui/icons-material/Public";

import {
  Table,
  TableBody,
  TableCell,
  FormControl,
  InputLabel,
  Pagination,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom"; // Importing navigate hook
import User_plus from "../../../shared/assets/icons/User_plus.jsx";
import axios from "axios";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter.jsx";
import GlobalButton from "../../../shared/utils/GlobalButton.jsx";
// 🔥 Spring Physics Keyframes
const springIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(80px) scale(0.92);
  }
  50% {
    opacity: 1;
    transform: translateY(-10px) scale(1.03);
  }
  70% {
    transform: translateY(5px) scale(0.98);
  }
  85% {
    transform: translateY(-2px) scale(1.01);
  }
  100% {
    transform: translateY(0px) scale(1);
  }
`;
// 🔥 Bounce Keyframes (inside same file)
const bounceIn = keyframes`
  0% {
    transform: scale(0.7);
    opacity: 0;
  }
  60% {
    transform: scale(1.08);
    opacity: 1;
  }
  80% {
    transform: scale(0.97);
  }
  100% {
    transform: scale(1);
  }
`;
// 🎯 Animation Keyframes
const fadeSlideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(60px) scale(0.9);
  }
  60% {
    opacity: 1;
    transform: translateY(-5px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0px) scale(1);
  }
`;
// 🔥 Custom Transition (same file)
const BounceTransition = React.forwardRef(
  function BounceTransition(props, ref) {
    return (
      <Grow
        ref={ref}
        {...props}
        timeout={400}
        style={{ transformOrigin: "center" }}
      />
    );
  },
);

// 🎯 Custom Transition Component
const SlideTransition = React.forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={350} />;
});

const CampaignTable = ({
  filteredCampaigns,
  adminsData,
  getCampaignList,
  isOrganizationId,
  isAllCampaignLoading,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedKam, setSelectedKam] = useState([]);

  function formatLocation(location) {
    const firstTwoStates = location.slice(0, 2).join(", ");
    const remainingStatesCount =
      location.length > 2 ? ` +${location.length - 2}` : "";
    return firstTwoStates + remainingStatesCount;
  }

  const getKAMName = (ids = []) => {
    // Step 1: Keep only matched KAMs
    const matchedKAMs = ids
      .map((id) => adminsData.find((k) => k._id === id))
      .filter(Boolean); // removes undefined/null

    // Step 2: Get first 2 names
    const kamNames = matchedKAMs.slice(0, 2).map((kam) => kam.name);

    // Step 3: Count only valid ones
    const extraKAMCount = matchedKAMs.length - 2;

    return (
      kamNames.join(", ") + (extraKAMCount > 0 ? ` +${extraKAMCount}` : "")
    );
  };

  const getAdminName = (ids = []) => {
    if (!ids.length) return "No Admin Assigned";

    const kamNames = ids
      .map((id) => {
        const kam = adminsData.find((k) => String(k._id) === String(id));
        return kam?.name;
      })
      .filter(Boolean)
      .slice(0, 2);

    const extraCount = ids.length - kamNames.length;

    return kamNames.join(", ") + (extraCount > 0 ? ` +${extraCount}` : "");
  };

  const handleViewDetails = (id) => {
    navigate(`/user/campaign/${id}`);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        overflowX: "hidden",
        height: "calc(100vh - 210px)",
      }}
    >
      <Table stickyHeader sx={{ minWidth: 1100 }}>
        {/* ---------- TABLE HEADER ---------- */}
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f5f7fa",
            }}
          >
            {[
              "Campaign Title",
              "Timeline",
              "Status",
              "Campaign Owner",
              ...(localStorage.getItem("userRole") == 1
                ? ["Admin Assign"]
                : []),
              "States",
              "Budget",
              "Progress",
              "Action",
            ].map((head) => (
              <TableCell
                key={head}
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  fontSize: "0.9rem",
                }}
              >
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* ---------- TABLE BODY ---------- */}
        {isAllCampaignLoading ? (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={9}
                sx={{ borderBottom: "none", height: "400px" }}
              >
                <div className="flex justify-center items-center h-full">
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : filteredCampaigns && filteredCampaigns.length > 0 ? (
          // Map through campaigns and display rows
          <TableBody>
            {filteredCampaigns?.map((campaign) => (
              <TableRow
                key={campaign._id}
                hover
                sx={{
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    transform: "scale(1.002)",
                  },
                }}
              >
                {/* Campaign Title */}
                <TableCell sx={{ fontWeight: 500 }}>
                  {campaign?.campaignTitle
                    ? campaign.campaignTitle.charAt(0).toUpperCase() +
                      campaign.campaignTitle.slice(1)
                    : "N/A"}
                </TableCell>

                {/* Timeline */}
                <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                  {campaign?.startDate
                    ? new Date(campaign.startDate).toLocaleDateString("en-GB")
                    : "N/A"}{" "}
                  -{" "}
                  {campaign?.endDate
                    ? new Date(campaign.endDate).toLocaleDateString("en-GB")
                    : "N/A"}
                </TableCell>

                {/* Status */}
                <TableCell>
                  {campaign.progress >= 0 && campaign.progress < 100 ? (
                    <Chip
                      label="Ongoing"
                      size="small"
                      sx={{
                        backgroundColor: "#e0f2fe",
                        color: "#0284c7",
                        fontWeight: 500,
                      }}
                    />
                  ) : (
                    <Chip
                      label="Complete"
                      size="small"
                      sx={{
                        backgroundColor: "#dcfce7",
                        color: "#16a34a",
                        fontWeight: 500,
                      }}
                    />
                  )}
                </TableCell>

                {/* Campaign Owner */}
                <TableCell sx={{ fontWeight: 500 }}>
                  {CapitalizeFirstLetter(campaign?.campaignOwnerId?.name) ||
                    "N/A"}
                </TableCell>

                {/* Admin Assign */}
                {localStorage.getItem("userRole") == 1 && (
                  <TableCell>
                    <Box>
                      <Tooltip title="Assign Admin">
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedCampaign(campaign);
                          }}
                        >
                          <User_plus className="relative z-10" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          campaign?.assignedClientIds?.length > 0 ? (
                            <div>
                              {campaign.assignedClientIds.map((id, i) => {
                                const admin = adminsData.find(
                                  (k) => String(k._id) === String(id),
                                );

                                return (
                                  <div key={id}>
                                    {i + 1}. {admin?.name || "Unknown Admin"}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            "No Admin Assigned"
                          )
                        }
                        arrow
                      >
                        <span style={{ display: "inline-block" }}>
                          {getKAMName(campaign?.assignedClientIds || [])}
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}

                {/* states */}
                <TableCell sx={{ fontWeight: 500, color: "#0f172a" }}>
                  <Tooltip title={campaign?.locations.join(", ")} arrow>
                    <span>{formatLocation(campaign?.locations || [])}</span>
                  </Tooltip>
                </TableCell>

                {/* Budget */}
                <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>
                  ₹ {campaign?.budget?.toLocaleString("en-IN") || "N/A"}
                </TableCell>

                {/* Progress */}
                <TableCell>
                  <Box>
                    <Typography variant="caption" fontWeight={500}>
                      {Math.round(campaign?.progress || 0)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={campaign?.progress || 0}
                      sx={{
                        height: 6,
                        borderRadius: 5,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </TableCell>

                {/* Action */}
                <TableCell align="center">
                  <GlobalButton
                    variant="primary"
                    size="small"
                    onClick={() => handleViewDetails(campaign._id)}
                  >
                    {`View`}
                  </GlobalButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        ) : (
          // Show "No Campaign Found" if list is empty
          <TableRow>
            <TableCell colSpan={9} sx={{ borderBottom: "none" }}>
              <div className="flex justify-center items-center h-[calc(100vh-300px)]">
                No Campaign found
              </div>
            </TableCell>
          </TableRow>
        )}
      </Table>
      <AssignAdminModal
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        getCampaignList={getCampaignList}
        adminsData={adminsData}
        campaignId={selectedCampaign?._id}
        campaignData={selectedCampaign}
        setSelectedKam={setSelectedKam}
        isOrganizationId={isOrganizationId}
        selectedKam={selectedKam}
      />
    </TableContainer>
  );
};

const AssignAdminModal = ({
  setAnchorEl,
  anchorEl,
  adminsData,
  campaignId,
  campaignData,
  setSelectedKam,
  getCampaignList,
  selectedKam,
  isOrganizationId,
}) => {
  const [campaignOwnerId, setCampaignOwnerId] = useState("");
  useEffect(() => {
    if (campaignData?.assignedClientIds) {
      setSelectedKam(campaignData.assignedClientIds);
    }
    setCampaignOwnerId(campaignData?.campaignOwnerId?._id);
  }, [campaignData]);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAssign = async () => {
    const serverURL = process.env.REACT_APP_URL;
    const token = localStorage.getItem("token");
    const validAdminIds = adminsData.map((admin) => admin._id);

    // Filter selectedKam to only include valid ones
    const filteredSelectedKam = selectedKam.filter((id) =>
      validAdminIds.includes(id),
    );

    try {
      const response = await axios.put(
        `${serverURL}/campaign/assignClientAdmins/${campaignId}`,
        {
          assignedClientIds: filteredSelectedKam, // 👈 sending selected KAMs
          campaignOwnerId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        // let organizationId = localStorage.getItem("organisationId");
        getCampaignList("", "", isOrganizationId, "");
        // setSelectedKam([]);
        setAnchorEl(null);
      }
    } catch (error) {
      console.error("Error assigning KAMs:", error);
    }
  };

  const toggleKam = (id) => {
    setSelectedKam((prev) =>
      prev.includes(id) ? prev.filter((k) => k != id) : [id, ...prev],
    );
  };
  const toggleCampaignOwnerId = (id) => {
    setCampaignOwnerId(id);
  };
  return (
    <Box className="flex justify-between items-center">
      <Dialog
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        keepMounted
        fullWidth
        maxWidth="md"
        sx={{
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
          "& .MuiPaper-root": {
            animation: `${springIn} 550ms cubic-bezier(0.22, 1.2, 0.36, 1)`,
          },
        }}
        PaperProps={{
          sx: {
            height: { sm: "80vh" },
            borderRadius: 4,
            backgroundColor: "#f8fafc",
            boxShadow: "0 30px 60px rgba(0,0,0,0.2)",
          },
        }}
      >
        {/* Fixed Header */}
        <DialogTitle
          sx={{
            // backgroundColor: "#d9dadb",
            display: "flex",
            justifyContent: "space-between",
            alignContent: "center",
          }}
        >
          <div>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Assign Admins
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.9rem",
              }}
            >
              Select the members you want to assign :
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col md:flex-row justify-between items-start w-full  md:h-[100%]  gap-3 ">
            <div
              className={` w-full
              border border-1 rounded-md p-3 shadow-sm  overflow-hidden h-full  bg-white`}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1e293b",
                }}
              >
                To the Campaign:
              </Typography>
              <div className="px-3 h-full overflow-auto mt-2">
                {adminsData.length == 0 ? (
                  <>
                    <div>
                      <p>There is no Admin</p>
                    </div>
                  </>
                ) : (
                  <>
                    {adminsData?.map((kam) => {
                      const isSelected = selectedKam?.some((k) => k == kam._id);

                      return (
                        <MenuItem
                          key={kam._id}
                          onClick={() => toggleKam(kam._id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                            transition: "all 0.2s ease",
                            border: isSelected
                              ? "1px solid #3b82f6"
                              : "1px solid #e5e7eb",
                            backgroundColor: isSelected
                              ? "rgba(59,130,246,0.08)"
                              : "#ffffff",
                            "&:hover": {
                              backgroundColor: "rgba(59,130,246,0.05)",
                            },
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                              }}
                            >
                              {CapitalizeFirstLetter(kam.name)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#555",
                                fontSize: "0.85rem",
                              }}
                            >
                              {kam.emailAddress}
                            </Typography>
                          </Box>
                          {/* <div className=" flex flex-col items-start justify-center">
                          {CapitalizeFirstLetter(kam.name)}
                        </div> */}
                          <Checkbox checked={isSelected} sx={{ ml: "auto" }} />
                        </MenuItem>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
            <div
              className={` w-full
              border border-1 rounded-md p-3 shadow-sm  overflow-hidden h-full  bg-white`}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1e293b",
                }}
              >
                To the Campaign Owner:
              </Typography>

              <div className="px-3 h-full overflow-auto mt-2">
                {adminsData.length == 0 ? (
                  <>
                    <div>
                      <p>There is no Admin</p>
                    </div>
                  </>
                ) : (
                  adminsData.map((kam) => {
                    const isSelected = campaignOwnerId === kam._id;

                    return (
                      <MenuItem
                        key={kam._id}
                        onClick={() => toggleCampaignOwnerId(kam._id)} // pass full object if needed
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          transition: "all 0.2s ease",
                          border: isSelected
                            ? "1px solid #3b82f6"
                            : "1px solid #e5e7eb",
                          backgroundColor: isSelected
                            ? "rgba(59,130,246,0.08)"
                            : "#ffffff",
                          "&:hover": {
                            backgroundColor: "rgba(59,130,246,0.05)",
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#333",
                            }}
                          >
                            {CapitalizeFirstLetter(kam.name)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#555",
                              fontSize: "0.85rem",
                            }}
                          >
                            {kam.emailAddress}
                          </Typography>
                        </Box>

                        {/* ✅ Radio instead of Checkbox */}
                        <Radio checked={isSelected} sx={{ ml: "auto" }} />
                      </MenuItem>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </DialogContent>

        {/* Fixed Footer */}
        <DialogActions
          sx={{
            p: 2,
            bottom: 0,
          }}
        >
          <GlobalButton onClick={handleClose}>Close</GlobalButton>

          <GlobalButton
            variant="primary"
            color="primary"
            onClick={handleAssign}
          >
            {`Assign`}
          </GlobalButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const CampaignApp = ({
  campaigns,
  adminsData,
  isAllCampaignLoading,
  getCampaignList,
  isOrganizationId,
  totalCampaignCount,
  allCampaigns,
}) => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("All"); // Default filter: "all"
  // const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId);
  const [searchQuery, setSearchQuery] = useState(""); // dropdown typing
  const [appliedSearch, setAppliedSearch] = useState(""); // table search
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 20, 50, 100];
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const [campaignSearchResults, setCampaignSearchResults] = useState([]);

  useEffect(() => {
    const results = allCampaigns.map((campaign) => ({
      id: campaign._id,
      title: campaign.campaignTitle,
      campaignOwner: {
        name: campaign.campaignOwnerId?.name || "N/A",
        email: campaign.campaignOwnerId?.emailAddress || "N/A",
      },
      locations: campaign.locations || [],
      cities: campaign.cities || [],
      storeNames: campaign.storeNames || [],
    }));

    setCampaignSearchResults(results);
  }, [allCampaigns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);

    return () => clearTimeout(timer); // cleanup
  }, [searchQuery]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (!isOrganizationId) return;

    getCampaignList(page, pageSize, isOrganizationId, appliedSearch);
  }, [page, pageSize, isOrganizationId, appliedSearch]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setAppliedSearch(searchQuery.trim());
      setPage(1); // reset pagination
      setIsSearchOpen(false); // close dropdown
      inputRef.current?.blur(); // ✅ correct blur
    }
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // reset to first page (VERY IMPORTANT)
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  // Filter and search campaigns
  const filteredCampaigns = campaigns.filter((campaign) => {
    const progress = campaign?.progress ?? 0;

    const matchesProgressFilter =
      filterStatus === "All" ||
      (filterStatus === "ongoing" && progress >= 0 && progress < 100) ||
      (filterStatus === "complete" && progress === 100);

    return matchesProgressFilter;
  });

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        inputRef.current?.blur();
        setAppliedSearch(searchQuery.trim());
        setPage(1); // reset pagination on escape as well
        setSearchQuery(""); // clear search input on escape
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const highlight = (text) => {
    if (!searchQuery) return text;
    const parts = text.split(new RegExp(`(${searchQuery})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <span key={i} className="bg-yellow-500 text-black rounded">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const buildSearchResults = (query, campaignSearchResults) => {
    if (!query) return [];

    const q = query.toLowerCase();
    const results = [];

    campaignSearchResults.forEach((campaign) => {
      // Campaign Title
      if (campaign.title?.toLowerCase().includes(q)) {
        results.push({
          id: campaign.id,
          type: "campaign",
          label: campaign.title,
          subLabel: "Campaign",
        });
      }

      // Owner
      if (campaign.campaignOwner?.name?.toLowerCase().includes(q)) {
        results.push({
          id: campaign.id,
          type: "owner",
          label: campaign.title,
          subLabel: `User • ${campaign.campaignOwner.name} - ${campaign.campaignOwner.email}`,
        });
      }

      // States
      campaign.locations?.forEach((state) => {
        if (state.toLowerCase().includes(q)) {
          results.push({
            id: campaign.id,
            type: "state",
            label: campaign.title,
            subLabel: `State • ${state}`,
          });
        }
      });

      // Cities
      campaign.cities?.forEach((city) => {
        if (city.toLowerCase().includes(q)) {
          results.push({
            id: campaign.id,
            type: "city",
            label: campaign.title,
            subLabel: `City • ${city}`,
          });
        }
      });

      // Stores
      campaign.storeNames?.forEach((store) => {
        if (store.toLowerCase().includes(q)) {
          results.push({
            id: campaign.id,
            type: "store",
            label: campaign.title,
            subLabel: `Store • ${store}`,
          });
        }
      });
    });

    return results;
  };
  const searchResults = useMemo(() => {
    return buildSearchResults(searchQuery, campaignSearchResults);
  }, [searchQuery, campaignSearchResults]);

  const handleViewDetails = (id) => {
    navigate(`/user/campaign/${id}`);
  };

  return (
    <div>
      {/* Header Section */}
      <Box
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" fontWeight="bold">
          All Campaigns
        </Typography>

        <Box display="flex" gap={2}>
          {/* Filter Dropdown */}
          <Select
            value={filterStatus}
            onChange={handleFilterChange}
            sx={{ minWidth: 150 }}
            size="small"
          >
            <MenuItem value="All">All Campaigns</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="ongoing">Ongoing</MenuItem>
          </Select>

          {/* Search Field */}

          <div className="relative w-[450px]" ref={searchRef}>
            <TextField
              inputRef={inputRef}
              placeholder="Search By Store, Title, Owner..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsSearchOpen(true)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Dropdown */}
            {isSearchOpen && searchQuery && (
              <div className="absolute top-[45px] left-0 w-full bg-[#f5f5f5] text-black rounded-xl shadow-xl border border-gray-700 z-50 overflow-hidden">
                {/* Results */}
                <div className="max-h-[300px] overflow-y-auto">
                  {searchResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleViewDetails(item.id)}
                      className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-200 cursor-pointer transition"
                    >
                      <div className="flex items-center gap-3">
                        {/* Icon based on type */}
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm">
                          {item.type === "campaign" && (
                            <CampaignOutlinedIcon fontSize="small" />
                          )}
                          {item.type === "owner" && (
                            <GroupIcon fontSize="small" />
                          )}
                          {item.type === "city" && (
                            <PlaceIcon fontSize="small" />
                          )}
                          {item.type === "state" && (
                            <PublicIcon fontSize="small" />
                          )}
                          {item.type === "store" && (
                            <StoreIcon fontSize="small" />
                          )}
                        </div>

                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {highlight(item.label)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {highlight(item.subLabel)}
                          </span>
                        </div>
                      </div>
                      <ArrowOutwardIcon fontSize="small" />
                    </div>
                  ))}
                  {searchResults.length === 0 && (
                    <div className="p-4 text-sm text-gray-500">
                      No results found
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-between text-xs text-gray-400 px-4 py-2 border-t border-gray-700">
                  <span>Press ↵ to select</span>
                  <span>ESC to close</span>
                </div>
              </div>
            )}
          </div>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <div className="ml-7 mt-5 w-full">
          <>
            <CampaignTable
              filteredCampaigns={filteredCampaigns}
              adminsData={adminsData}
              getCampaignList={getCampaignList}
              isOrganizationId={isOrganizationId}
              isAllCampaignLoading={isAllCampaignLoading}
            />
            <div className="flex items-center justify-between ">
              <FormControl
                variant="standard"
                size="small"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  "& .MuiInputBase-root:before": { borderBottom: "none" },
                  "& .MuiInputBase-root:after": { borderBottom: "none" },
                  "& .MuiInputBase-root": { marginTop: 1 }, // 👈 override here
                }}
              >
                <InputLabel
                  id="page-size-label"
                  sx={{
                    position: "static",
                    transform: "none",
                    fontSize: "14px",
                    marginRight: "8px",
                    marginTop: "2px",
                  }}
                >
                  Rows
                </InputLabel>
                <Select
                  labelId="page-size-label"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Rows"
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                className="p-2 flex justify-center"
                count={Math.ceil(totalCampaignCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "#418cff", // Blue background for selected
                    color: "#fff", // White text for selected
                  },
                  "& .MuiPaginationItem-root.Mui-selected:hover": {
                    backgroundColor: "#3366cc", // Darker blue on hover
                  },
                  "& .MuiPaginationItem-root": {
                    "&:hover": {
                      backgroundColor: "#ddd", // Light gray for hovered items
                    },
                  },
                }}
              />
            </div>
          </>
        </div>
      </Grid>

      {/* <RecceApprovalTable open={isModalOpen} campaignProductData={selectedCampaign?.products || []} onClose={handleCloseModal} /> */}
    </div>
  );
};

export default CampaignApp;
